import React, { useRef } from 'react';
import { useAtomValue } from 'jotai/index';

import {
    _tileMediaVariants,
    TileMedia,
} from '@rocco/ui/tile-shared/components/TileMedia';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { ProfessionalTileLogo } from './ProfessionalTileLogo';
import { ProfessionalTileServices } from './ProfessionalTileServices';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '@archipro-website/web/app/modules/tracking/util/trackTileMedia';
import { useABTestIsOn } from '@archipro-website/web/app/modules/root/hook/use-growthbook';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { cn } from '@rocco/utils/cn';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';

export const ProfessionalTile = (
    props: TileViewModel<ProfessionalTileViewModel> & {
        hideServiceTags?: boolean;
        rounded?: boolean;
    }
) => {
    const {
        aspectRatio,
        model: professional,
        section,
        isFollowed,
        onFollow,
        hideFollowButton = false,
        hideServiceTags = false,
        // rounded,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
    } = props as TileViewModel<ProfessionalTileViewModel> & {
        hideCategory?: boolean;
        rounded?: boolean;
    };

    const tileActions = useAtomValue(uiContextAtomTileActions);

    const tileRef = useRef<HTMLAnchorElement>(null);
    const isSponsored = professional.services.includes('Sponsored');
    const tracker = useTracker();
    const isTileMediaSlider = useABTestIsOn('tile-media-slider');

    const handleTileClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        e.stopPropagation();

        onClick?.();
        if (disableLink) {
            return;
        }
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            professional.id,
            undefined,
            section
        );

        trackTileMediaDetailClick(tracker, {
            itemID: professional.id,
            itemType: 'ProfessionalTile',
            itemName: professional.title,
            variant: isTileMediaSlider ? 'A' : 'B',
        });
    };
    const tileContent = (
        <div className={cn('flex flex-col gap-4', className)}>
            <div
                className={`group relative ${_tileMediaVariants({ aspectRatio })}`}
            >
                <TileMedia
                    aspectRatio={aspectRatio}
                    images={[professional.image]}
                    video={professional.video}
                    onSlideChange={(direction) => {
                        tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                            direction,
                            professional.id
                        );
                    }}
                />

                <div className="absolute inset-0 hidden rounded-tile bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100" />

                {professional.logo && (
                    <div className="absolute left-2.5 top-2 md:left-4 md:top-4">
                        <ProfessionalTileLogo
                            image={professional.logo.image}
                            background={professional.logo.background}
                        />
                    </div>
                )}

                <div className="absolute right-4 top-3.5 flex gap-1 transition-opacity duration-300 md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100">
                    {!hideFollowButton && (
                        <TileFollowButton
                            pinId={professional.id}
                            pinType="ProfessionalPin"
                            isFollowed={isFollowed}
                            onFollow={onFollow}
                        />
                    )}
                </div>
                {isSponsored && (
                    <div className="text-md absolute right-0 mt-3 text-gray-900/40">
                        <span>Sponsored</span>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4">
                {inlineActionButtons && (
                    <div className="flex flex-col">{inlineActionButtons}</div>
                )}
                <div
                    className={`${TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK} flex flex-col md:gap-0.5`}
                >
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm font-medium tracking-[0.03rem] md:h-[1.4rem] md:text-base md:tracking-[0.02rem]">
                        {professional.title}
                    </span>
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm font-medium tracking-[0.03rem] text-gray-900/40 md:h-[1.4rem] md:text-base md:tracking-[0.02rem]">
                        {professional.category}
                    </span>
                </div>

                {!hideServiceTags && (
                    <>
                        <div className="hidden justify-start gap-2 md:flex">
                            <ProfessionalTileServices
                                services={professional.services}
                            />
                        </div>
                        <div className="flex justify-start gap-2 md:hidden">
                            <ProfessionalTileServices
                                limit={1}
                                services={professional.services}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    const linkProps = disableLink
        ? {
              mode: 'override' as const,
          }
        : {
              mode: 'normal' as const,
              prefetch: 'intent' as const,
          };

    return (
        <OptionalLink
            to={professional.link}
            onClick={handleTileClick}
            ref={tileRef}
            {...linkProps}
        >
            {tileContent}
        </OptionalLink>
    );
};
